import { useEffect, useRef } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { Text, Toggle } from '@cencosud-cencommerce/paris-uikit';

import { IS_AUTOCOMPLETE_ENABLED } from '@/constants';
import { SET_VALUE_OPTIONS } from '@/sections/address/components/AddressForm/Autocomplete/constants';
import type { AddressFormValues } from '@/sections/address/types/AddressFormValues';
import { usePlaceData } from '@/sections/region/GooglePlaceDataContext';

const LABEL_ENTER_ADDRESS_MANUALLY = 'Ingresar dirección manualmente';

const clearLocationValues = (setValue: UseFormSetValue<AddressFormValues>) => {
  setValue('latitude', undefined, SET_VALUE_OPTIONS);
  setValue('longitude', undefined, SET_VALUE_OPTIONS);
  setValue('postalCode', undefined, SET_VALUE_OPTIONS);
  setValue('plusCode', undefined, SET_VALUE_OPTIONS);
};

export const ToggleAutocomplete = () => {
  const { control, clearErrors, watch, setValue, resetField } =
    useFormContext<AddressFormValues>();
  const { setPlaceData } = usePlaceData();
  const isInitialRender = useRef(true);
  const prevIsAutocomplete = useRef<boolean | undefined>(undefined);

  const skipStreetName = watch('_skipResetFields');
  const isAutocomplete = watch('isAutocomplete');

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      prevIsAutocomplete.current = isAutocomplete;
      return;
    }

    setPlaceData(null);
    clearLocationValues(setValue);

    if (skipStreetName) {
      resetField('streetNumber');
    } else {
      setTimeout(() => {
        resetField('streetName');
        resetField('streetNumber');
      }, 150);
    }

    prevIsAutocomplete.current = isAutocomplete;
  }, [
    clearErrors,
    isAutocomplete,
    resetField,
    setPlaceData,
    setValue,
    skipStreetName
  ]);

  useEffect(() => {
    if (isAutocomplete && skipStreetName) {
      setValue('_skipResetFields', false, SET_VALUE_OPTIONS);
    }
  }, [isAutocomplete, resetField, setValue, skipStreetName]);

  if (!IS_AUTOCOMPLETE_ENABLED) return null;

  return (
    <div className="co-flex co-flex-row co-gap-[8px] co-items-center">
      <Controller
        name="isAutocomplete"
        control={control}
        render={({ field }) => (
          <Toggle
            checked={!field.value}
            onChange={() => {
              setValue('isAutocomplete', !field.value, SET_VALUE_OPTIONS);
            }}
          />
        )}
      />

      <Controller
        name="_skipResetFields"
        control={control}
        render={({ field }) => (
          <input type="hidden" {...field} value={String(field.value)} />
        )}
      />
      <Text variant="body2" className="co-text-[12px] co-leading-[16px]">
        {LABEL_ENTER_ADDRESS_MANUALLY}
      </Text>
    </div>
  );
};

ToggleAutocomplete.displayName = 'ToggleAutocomplete';

export default ToggleAutocomplete;
